import { type FC, useState } from "react";

import { faCheck } from "@fortawesome/pro-duotone-svg-icons/faCheck";
import { faCopy } from "@fortawesome/pro-duotone-svg-icons/faCopy";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useClipboard } from "@application/hooks/use_clipboard";

import { track } from "@application/utilities/track";

import { Button } from "@styled/button";
import { DateTimeFormatter } from "@styled/date_time_formatter";
import { HStack } from "@styled/h_stack";
import { InlineNotice } from "@styled/inline_notice";
import { LinkButton } from "@styled/link_button";
import { NoticeLine } from "@styled/notice_line";
import { ScreenReaderStatus } from "@styled/screen_reader_status";
import { Stack } from "@styled/stack";
import { Well } from "@styled/well";

import { type IntakeProject, type Interview } from "./types";
import { useIntakeInterviewActions } from "./use_intake_interview_actions";

// The claimant's link to copy into the office's own email or text, and a way
// to make a new one. A link that has expired or locked shows only the latter.
export const IntakeLinkBlock: FC<{
  project: IntakeProject;
  interview: Interview;
}> = ({ project, interview }) => {
  if (!project.policy.modify) return null;

  return interview.linkUrl ? (
    <OpenLink
      projectId={project.id}
      interview={interview}
      linkUrl={interview.linkUrl}
    />
  ) : (
    <ClosedLink projectId={project.id} />
  );
};

const OpenLink: FC<{
  projectId: string;
  interview: Interview;
  linkUrl: string;
}> = ({ projectId, interview, linkUrl }) => {
  const { copied, copy } = useClipboard(linkUrl);
  const [confirming, setConfirming] = useState(false);

  return (
    <Stack gap="lg">
      <Well>{linkUrl}</Well>
      {!confirming && (
        <HStack gap="lg" align="center">
          {/* The icon changes, not the words, so nothing beside it moves. */}
          <Button
            color="navy"
            onClick={() =>
              void copy().then(() =>
                track("intake_link_copied", { project_id: projectId }),
              )
            }
          >
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} fixedWidth /> Copy
            link
          </Button>
          <ScreenReaderStatus>{copied && "Link copied"}</ScreenReaderStatus>
          {/* LinkButton aligns itself to the top; its own box centres it. */}
          <span>
            <LinkButton onClick={() => setConfirming(true)}>
              Make a new link
            </LinkButton>
          </span>
        </HStack>
      )}
      <NoticeLine>
        {interview.linkReplaced ? "New link made" : "Made"}{" "}
        <DateTimeFormatter iso={interview.linkCreatedAt} format="date" />.
        {interview.linkReplaced && " The old one no longer works."}
      </NoticeLine>
      {confirming && (
        <ConfirmNewLink
          projectId={projectId}
          onDone={() => setConfirming(false)}
        />
      )}
    </Stack>
  );
};

// A new link after the claimant declined: they see the notice and agree again.
export const DeclinedNewLink: FC<{ projectId: string }> = ({ projectId }) => {
  const [confirming, setConfirming] = useState(false);

  return confirming ? (
    <ConfirmNewLink projectId={projectId} onDone={() => setConfirming(false)} />
  ) : (
    <LinkButton onClick={() => setConfirming(true)}>Make a new link</LinkButton>
  );
};

const ConfirmNewLink: FC<{ projectId: string; onDone(): void }> = ({
  projectId,
  onDone,
}) => {
  const { makeLink, linking } = useIntakeInterviewActions(projectId);

  return (
    <InlineNotice
      title="Make a new link?"
      body="The link you sent stops working."
      action={
        <HStack gap="md">
          <Button
            color="navy"
            loading={linking}
            onClick={() => void makeLink().then(onDone)}
          >
            Make a new link
          </Button>
          <Button color="white" onClick={onDone}>
            Cancel
          </Button>
        </HStack>
      }
    />
  );
};

// The old link has expired or locked, so there's nothing to confirm.
const ClosedLink: FC<{ projectId: string }> = ({ projectId }) => {
  const { makeLink, linking } = useIntakeInterviewActions(projectId);

  return (
    <Stack gap="md">
      <NoticeLine>
        This link no longer works. Make a new link and send it to the claimant.
      </NoticeLine>
      <Button color="navy" loading={linking} onClick={() => void makeLink()}>
        Make a new link
      </Button>
    </Stack>
  );
};
